<script setup lang="ts">
    import { type ProductTyping, type SmallCardTyping } from '@/typings/widget';
    import { type IdTyping } from '@shared/typings';
    import Card from './Card.ce.vue';

    const props = defineProps<{
        modelValue: IdTyping | IdTyping[]
        title?: string
        items?: ProductTyping[]
        multiple?: boolean
        skip?: boolean
        showPrice?: boolean
        skipText?: string
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<{
        (event: 'update:modelValue', value: number | number[]): void
        (event: 'next' | 'skip'): void
    }>();

    const { t } = useI18n();

    const cardsModel: Ref<number | number[] | null> = ref(props.modelValue ?? null);

    const handleSelectedCard = async(card: SmallCardTyping): Promise<void> => {
        if (props.multiple) {
            const cardIndex = (cardsModel.value as number[]).findIndex(crd => crd === card.id);
            if (cardIndex > -1) {
                (cardsModel.value as number[]).splice(cardIndex, 1);
            } else {
                (cardsModel.value as number[]).push(card.id);
            }
        // eslint-disable-next-line no-negated-condition
        } else {
            cardsModel.value = card.id;
            await nextTick(() => {
                emit('next');
            });
        }
    };

    watch(() => cardsModel.value, nVal => {
        if (nVal) emit('update:modelValue', nVal);
    });
</script>

<template>
    <div
        :class="{'quote-cards--multiple': (cardsModel as number[])?.length > 0 && multiple}"
        class="quote-cards"
    >
        <span class="quote-cards__title">
            {{ title }}
        </span>
        <div class="quote-cards__container">
            <Card
                v-for="(item, index) in items"
                :key="`${item.id}-${index}`"
                :item="item"
                :selected="multiple ? (cardsModel as number[]).includes(item.id) : cardsModel === item.id"
                :show-price="showPrice"
                @click="handleSelectedCard(item)"
            />
        </div>
        <button
            v-if="(cardsModel as number[])?.length > 0 && multiple"
            type="button"
            class="btn btn--primary quote-cards__next"
            @click="emit('next')"
        >
            <span>
                {{ t('widget.buttons.next') }}
            </span>
        </button>
        <button
            v-if="skip && !(cardsModel as number[])?.length"
            type="button"
            class="btn btn--secondary quote-cards__skip"
            @click="emit('skip')"
        >
            <span>
                {{ skipText }}
            </span>
        </button>
    </div>
</template>

<style lang="scss" scoped>
.quote-cards {
    position: relative;
    display: flex;
    flex-direction: column;
    padding-block-start: var(--spacing-03);

    @include min-width(md) {
        padding-block-start: var(--spacing-05);
    }

    @include min-width(lg) {
        padding-block-start: var(--spacing-02);
    }

    &--multiple {
        padding-block-end: var(--spacing-12);

        @include min-width(lg) {
            padding-block-end: 0;
        }
    }

    &__title {
        color: var(--text-primary);
        font-family: var(--font-primary);
        font-size: var(--font-h4);
        font-weight: var(--font-weight-medium);
        line-height: var(--line-height-150);
        padding-inline: var(--spacing-05);
        text-align: center;
    }

    &__container {
        display: grid;
        overflow: auto;
        block-size: 100%;
        gap: var(--spacing-02);
        grid-template-columns: repeat(2, 1fr);
        inline-size: 100%;
        padding-block: var(--spacing-04) var(--spacing-16);
        padding-inline: var(--spacing-04);

        @include min-width(md) {
            grid-template-columns: repeat(4, 1fr);
            padding-block: var(--spacing-07) var(--spacing-07);
            padding-inline: var(--spacing-07);
        }

        @include min-width(lg) {
            padding-block: var(--spacing-09) var(--spacing-07);
            padding-inline: var(--spacing-12);
        }
    }

    &__next,
    &__skip {
        position: fixed;
        z-index: var(--zindex-stack-twice);
        align-self: center;
        inset-block-end: var(--spacing-05);
        inset-inline-start: 50%;
        min-inline-size: 174px;
        transform: translateX(-50%);

        @include min-width(lg) {
            margin-block-end: var(--spacing-06);
            transform: none;
        }
    }

    &__next {
        @include min-width(lg) {
            position: static;
            inset-inline-start: 0;
        }
    }

    &__skip {
        @include min-width(lg) {
            inset-block-end: var(--spacing-04);
            inset-inline: auto var(--spacing-04);
            margin-block-end: 0;
        }
    }
}
</style>
